import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  FormControlLabel,
  FormHelperText,
  Switch,
} from "@mui/material";
import FlexBox from "components/FlexBox";
import LightTextField from "components/LightTextField";
import { H1, Paragraph, Small } from "components/Typography";
import { appInfoLoader } from "components/authentication/AppInfoLoader";
import { TextFieldWrapper } from "components/authentication/StyledComponents";
import { useFormik } from "formik";
import useAuth from "hooks/useAuth";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import CompanyAPI, { Company } from "apis/CompanyAPI";
import LoadingScreen from "components/LoadingScreen";

const ExternalLogin: FC = () => {
  const { customerLogin } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  let navigate = useNavigate();
  const { companyURL } = useParams();
  const { t } = useTranslation();

  const [company, setCompany] = useState<Company | null>(null);

  interface LoginFormValues {
    email: string;
    password: string;
    submit: null;
    remember: boolean;
  }

  const initialValues: LoginFormValues = {
    email: "",
    password: "",
    submit: null,
    remember: true,
  };
  // form field value validation schema
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("erroEmailInvalido"))
      .max(255)
      .required(t("erroEmailRequerido")),
    password: Yup.string()
      .min(6, t("erroSenhaTamanhoMinimo"))
      .required(t("erroSenhaRequerido")),
  });

  const { errors, values, touched, handleBlur, handleChange, handleSubmit } =
    useFormik<LoginFormValues>({
      initialValues,
      validationSchema,
      onSubmit: (values) => {
        setLoading(true);
        customerLogin(values.email, values.password)
          .then((response) => {
            setLoading(false);
            appInfoLoader();
            navigate("/customer-access");
          })
          .catch((error: { detail?: string; message?: string }) => {
            setError(error.detail || error.message || "Falha ao autenticar.");
            setLoading(false);
          });
      },
    });

  useEffect(() => {
    if (companyURL) {
      setTimeout(() => {
        loadCompany(companyURL);
      }, 2000);
    }
  }, [companyURL]);

  const loadCompany = (companyURL: string) => {
    CompanyAPI.getByExternalLoginURL(companyURL).then((response) => {
      const result = response.data as Company;
      setCompany(result);
    });
  };

  const getRegisterURL = () => {
    if (!company) return "/register";
    return `/customer-register/company/${company.externalLoginURL}`;
  };

  if (!company) return <LoadingScreen />;

  return (
    <FlexBox
      sx={{
        alignItems: "center",
        flexDirection: "column",
        justifyContent: "center",
        height: { sm: "100%" },
      }}
    >
      <Card sx={{ padding: 4, maxWidth: 600, boxShadow: 1 }}>
        <FlexBox
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
          mb={5}
        >
          <Box width={150} mb={1}>
            {company?.photo ? (
              <img src={company.photo} width="100%" alt="Uko Logo" />
            ) : (
              <img src="/static/logo/logo.svg" width="100%" alt="Uko Logo" />
            )}
          </Box>
          <H1 fontSize={24} fontWeight={700}>
            {t("Entrar na")} {company.name}
          </H1>
        </FlexBox>

        <FlexBox justifyContent="space-between" flexWrap="wrap" my="1rem">
          <form noValidate onSubmit={handleSubmit} style={{ width: "100%" }}>
            <FlexBox justifyContent="space-between" flexWrap="wrap">
              <TextFieldWrapper>
                <Paragraph fontWeight={600} mb={1}>
                  Email
                </Paragraph>
                <LightTextField
                  fullWidth
                  name="email"
                  type="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.email || ""}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                />
              </TextFieldWrapper>

              <TextFieldWrapper>
                <Paragraph fontWeight={600} mb={1}>
                  Senha
                </Paragraph>
                <LightTextField
                  fullWidth
                  name="password"
                  type="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password || ""}
                  error={Boolean(touched.password && errors.password)}
                  helperText={touched.password && errors.password}
                />
              </TextFieldWrapper>
            </FlexBox>

            <FlexBox mt={2} alignItems="center" justifyContent="space-between">
              <FormControlLabel
                control={
                  <Switch
                    name="remember"
                    checked={values.remember}
                    onChange={handleChange}
                  />
                }
                label={t("Lembrar-me")}
                sx={{ "& .MuiTypography-root": { fontWeight: 600 } }}
              />
              <Link to="/forget-password">
                <Small color="secondary.red">{t("Esqueceu a senha?")}</Small>
              </Link>
            </FlexBox>

            {error && (
              <FormHelperText
                error
                sx={{
                  mt: 2,
                  fontSize: 13,
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                {error}
              </FormHelperText>
            )}

            <Box sx={{ mt: 4 }}>
              {loading ? (
                <LoadingButton loading fullWidth variant="contained">
                  {t("Entrando...")}
                </LoadingButton>
              ) : (
                <Button fullWidth type="submit" variant="contained">
                  {t("Entrar")}
                </Button>
              )}
            </Box>
          </form>

          <Small margin="auto" mt={3} color="text.disabled">
            {t("Não possui conta?")}{" "}
            <Link to={getRegisterURL()}>
              <Small color="primary.main">{t("Criar conta")}</Small>
            </Link>
          </Small>
        </FlexBox>
      </Card>
    </FlexBox>
  );
};

export default ExternalLogin;
